import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { axiosInstance, BASE_URL } from "../Config";
import { FaEdit } from "react-icons/fa";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";

const Profile = () => {
  const [data, setData] = useState({
    name: "",
    email: "",
    phone_number: "",
    image: "",
    country_code: ""
  });
  const [phoneValue, setPhoneValue] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfileData = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("No token found. Please log in again.");
        return;
      }

      try {
        const response = await axiosInstance.get(`/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
     
        if (response.data && response.data.body) {
          const profile = response.data.body;
          setData(profile);

          const cc = profile.country_code || profile.countryCode || "";
          const pn = profile.phone_number || profile.phoneNumber || "";
          setPhoneValue(`${cc}${pn}`);

          const imageUrl = profile.image?.startsWith("http")
            ? profile.image
            : `${BASE_URL}/${profile.image}`;
          setImagePreview(imageUrl);
        }
      } catch (error) {
        // console.error("Error fetching profile:", error);
      }
    };

    fetchProfileData();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const validateName = (name) => {
    if (!name.trim()) return "Name is required";
    if (name.startsWith(" ")) return "Name cannot start with a space";

    const nameRegex = /^[A-Za-z ]+$/;
    if (!nameRegex.test(name)) {
      return "Name can contain only alphabets";
    }

    if (name.length < 2 || name.length > 20) {
      return "Name must be between 2 and 20 characters";
    }

    return "";
  };

  const validatePhone = (phone_number) => {
    const phoneRegex = /^[0-9]+$/;
    if (!phone_number) return "Phone number is required";
    if (phone_number.length < 6 || phone_number.length > 15)
      return "Phone number must be 6-15 digits";
    if (!phoneRegex.test(phone_number))
      return "Phone number must contain only numbers";
    return "";
  };



  const handleNameChange = (e) => {
    const value = e.target.value;

    if (/^[A-Za-z ]*$/.test(value)) {
      setData((prevData) => ({ ...prevData, name: value }));

      const nameError = validateName(value);
      setErrors((prevErrors) => ({ ...prevErrors, name: nameError }));
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    let formErrors = {};
    const dial = selectedCountry?.country?.dialCode ? `+${selectedCountry.country?.dialCode}` : "";

    const allDigits = (phoneValue || "").replace(/\D/g, "");
    const dialDigits = (selectedCountry?.country?.dialCode || "").replace(/\D/g, "");

    const phone_number = allDigits.startsWith(dialDigits)
      ? allDigits.slice(dialDigits.length)
      : allDigits;

    const country_code = dial;

    const phoneError = validatePhone(phone_number);
    if (phoneError) formErrors.phone_number = phoneError;
    const nameError = validateName(data.name);
    if (nameError) formErrors.name = nameError;

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No token found. Please log in again.");
      return;
    }

    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("email", data.email);
    formData.append("country_code", country_code);
    formData.append("phone_number", phone_number);
    if (selectedImage) {
      formData.append("image", selectedImage);
    }

    try {
      const response = await axiosInstance.post(`/updateprofile`, formData);

      const updatedData = response.data.body;
      setData(updatedData);
      if (updatedData.image) {
        const imageUrl = updatedData.image.startsWith("http")
          ? updatedData.image
          : `${BASE_URL}/${updatedData.image}`;
        setImagePreview(imageUrl);
      }
      localStorage.setItem("userData", JSON.stringify(updatedData));
      toast.success("Profile updated successfully");
      navigate("/dashboard", { state: { updated: true } });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error updating profile");
    }
  };

  return (
    <>
      <div id="layout-wrapper">
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="title-box mb-3 pb-1">
                <h4 className="mb-0 page-title">Edit Profile</h4>
              </div>
              <div className="row justify-content-center">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="text-center mb-4">
                        <div className="position-relative d-inline-block">
                          <img
                            src={imagePreview || ""}
                            style={{
                              width: 200,
                              height: 200,
                              objectFit: "cover",
                              borderRadius: "20%",
                            }}
                            alt="Profile"
                          />
                          <input
                            type="file"
                            accept=".jpg,.png,.jpeg"
                            id="fileInput"
                            style={{ display: "none" }}
                            onChange={handleImageChange}
                          />
                          <label
                            htmlFor="fileInput"
                            className="position-absolute bottom-0 end-0 bg-primary text-white rounded-circle p-2"
                            style={{ cursor: "pointer" }}
                          >
                            <FaEdit size={20} />
                          </label>
                        </div>
                      </div>
                      <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                          <label className="mb-1 fw-medium">Name</label>
                          <input
                            type="text"
                            className="form-control"
                            name="name"
                            value={data.name}
                            onChange={handleNameChange}
                            maxLength={50}
                          />
                          {errors.name && (
                            <div className="text-danger">{errors.name}</div>
                          )}
                        </div>

                        <div className="mb-3">
                          <label className="mb-1 fw-medium">Phone Number</label>

                          <PhoneInput
                            defaultCountry="us"
                            value={phoneValue}
                            onChange={(value, country) => {
                              setPhoneValue(value);
                              setSelectedCountry(country);

                              const dialDigits = String(country?.country?.dialCode || "").replace(/\D/g, "");

                              const allDigits = String(value || "").replace(/\D/g, "");

                              const phoneDigits = allDigits.startsWith(dialDigits)
                                ? allDigits.slice(dialDigits.length)
                                : allDigits;

                              if (phoneDigits.length < 6 || phoneDigits.length > 15) {
                                setErrors((p) => ({ ...p, phone_number: "Phone number must be 6-15 digits" }));
                              } else {
                                setErrors((p) => ({ ...p, phone_number: "" }));
                              }
                            }}
                            style={{ width: "100%" }}
                            inputStyle={{ width: "100%" }}
                          />

                          {errors.phone_number && (
                            <div className="text-danger">{errors.phone_number}</div>
                          )}
                        </div>

                        <div className="mb-3">
                          <label className="mb-1 fw-medium">Email</label>
                          <input
                            type="email"
                            className="form-control"
                            name="email"
                            value={data.email}
                            disabled
                          />
                        </div>

                        <div className="text-end mb-2">
                            <button
                              type="button"
                              className="btn btn-secondary me-2 px-4"
                              onClick={() => navigate(-1)}
                            >
                              Back
                            </button>
                          <button
                            type="submit"
                            className="btn btn-danger px-4"
                          >
                            Update
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Profile;